import React, { useMemo, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Chip,
    Divider,
    Box,
    Grid,
    Stack,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BlockIcon from "@mui/icons-material/Block";

import { useAuth } from "../components/TokenHandler";

const serviceOptions = ["CCM", "RPM Passive", "RPM Active", "BHI"];
const prettyDate = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "—");

const formatIdList = (arr, max = 20) => {
    if (!Array.isArray(arr) || arr.length === 0) return "—";
    const clean = arr
        .map((x) => (x == null ? null : String(x).trim()))
        .filter(Boolean);

    if (clean.length <= max) return clean.join(", ");
    return `${clean.slice(0, max).join(", ")}, … (+${clean.length - max})`;
};

const buildDeactivatedApiBits = (da) => {
    if (!da) return [];
    return [
        da.source_system ? `Source: ${da.source_system}` : null,
        da.account_status ? `Status: ${da.account_status}` : null,
        da.facility_unique_id != null ? `Facility ID: ${da.facility_unique_id}` : null,
    ].filter(Boolean);
};


const initialForm = () => ({
    facility_unique_id: "",
    service_name: "",
    start_date: dayjs(),
    rep_name: "",
    original_creator: "",
    notes: "",
});

const statusColor = (statusRaw) => {
    const status = String(statusRaw || "").toLowerCase();
    switch (status) {
        case "active":
            return "success";
        case "on hold":
            return "warning";
        case "failed launch":
            return "error";
        case "ended":
        default:
            return "default";
    }
};

const statusLabel = (statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();
    if (s === "active") return "Active";
    if (s === "on hold") return "On Hold";
    if (s === "failed launch") return "Failed Launch";
    if (s === "ended") return "Ended";
    return statusRaw || "—";
};

// Map the single selection to API payload keys
// dateMode: 'none' | 'restart' | 'stop' | 'hold' (and legacy 'start')
const mapSingleDateToPayload = (dateMode, dateValue) => {
    const out = {};
    if (!dateValue) return out;
    const val = dateValue?.toDate ? dateValue.toDate() : dateValue;

    if (dateMode === "start" || dateMode === "restart") out.start_date = val;
    if (dateMode === "stop") out.stop_date = val;
    if (dateMode === "hold") out.on_hold_date = val;

    return out;
};

export default function FacilityServiceDetailsModal({ open, onClose, facility }) {

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    const [updateServiceForms, setUpdateServiceForms] = useState({});
    const [expandedService, setExpandedService] = useState(null);
    const [localFacility, setLocalFacility] = useState(facility);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(initialForm());

    const resetNewServiceForm = () => setFormData(initialForm());

    const handleDialogClose = () => {
        resetNewServiceForm();
        setShowForm(false);
        setExpandedService(null);
        setUpdateServiceForms({});
        onClose?.();
    };

    const handleCancelNew = () => {
        resetNewServiceForm();
        setShowForm(false);
    };

    const handleToggleNewForm = () => {
        if (showForm) resetNewServiceForm();
        setShowForm((s) => !s);
    };

    useEffect(() => {
        setLocalFacility(facility || null);
        if (!open) {
            resetNewServiceForm();
            setShowForm(false);
            setExpandedService(null);
            setUpdateServiceForms({});
        }
    }, [facility, open]);

    const groupedServices = useMemo(() => {
        const map = new Map();
        const allServices = localFacility?.services || [];
        for (const service of allServices) {
            const name = service.service_name;
            map.set(name, [...(map.get(name) || []), service]);
        }
        for (const [name, entries] of map.entries()) {
            entries.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
            map.set(name, entries);
        }
        return map;
    }, [localFacility]);

    const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

    const handleUpdateServiceChange = (id, field, value) => {
        setUpdateServiceForms((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [field]: value },
        }));
    };

    const handleUpdateServiceSubmit = async (id) => {
        const form = updateServiceForms[id];
        if (!form?.notes || !form.notes.trim()) {
            alert("Notes are required.");
            return;
        }

        const mode = form.dateMode; // 'none' | 'restart' | 'stop' | 'hold'

        if (mode === "restart" && !form.selected_date) {
            alert("New start date is required to restart the service.");
            return;
        }

        const payload = {
            id,
            notes: { text: form.notes, creator: form.creator || "" },
            facility_name: facility.facility_name,
            fac_id: facility.fac_id,
            ...mapSingleDateToPayload(mode, form.selected_date || null),
        };

        if (mode === "restart") {
            payload.restart = true;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/facility/update-service`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${kc.token}` },
                    body: JSON.stringify(payload),
                }
            );
            if (!response.ok) throw new Error(`Failed: ${response.status}`);
            const data = await response.json();

            setLocalFacility((prev) => {
                const services = prev?.services || [];

                if (mode === "restart") {
                    // Mark old record inactive in local state and append the new record
                    const updatedServices = services.map((s) =>
                        s.id === id ? { ...s, active: false } : s
                    );
                    return {
                        ...prev,
                        services: [...updatedServices, data.service],
                    };
                }

                // Normal update: replace the updated record
                const updatedServices = services.map((s) =>
                    s.id === data.service.id ? data.service : s
                );
                return { ...prev, services: updatedServices };
            });

            setUpdateServiceForms((prev) => ({ ...prev, [id]: null }));
        } catch (e) {
            console.error(e);
            alert("Failed to update service.");
        }
    };

    const handleSubmitNew = async () => {
        const statusOf = (s) => String(s.service_status || "").trim().toLowerCase();
        const isBlocking = (s) =>
            statusOf(s) === "active" ||
            statusOf(s) === "pending approval" ||
            (!!s.start_date && !s.stop_date && !s.on_hold_date);

        const inUseBlocking = (localFacility?.services || []).some(
            (s) => s.service_name === formData.service_name && isBlocking(s)
        );

        // Only block when trying to create a new Active (dateMode === "start")
        if (inUseBlocking && formData.dateMode === "start") {
            alert(
                "This service is already active or pending approval. You cannot create another active entry."
            );
            return;
        }

        const newServicePayload = {
            facility_unique_id: facility.facility_unique_id,
            monday_itemid: facility.monday_itemid,
            fac_id: facility.fac_id,
            facility_name: facility.facility_name,
            service_name: formData.service_name,
            start_date: formData.start_date?.toDate
                ? formData.start_date.toDate()
                : formData.start_date,
            rep: formData.rep_name,
            notes: { text: formData.notes, creator: formData.original_creator },
            original_creator: formData.original_creator,
        };

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/facility/create-service`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${kc.token}` },
                    body: JSON.stringify(newServicePayload),
                }
            );
            if (!response.ok) throw new Error(`Server responded ${response.status}`);
            const data = await response.json();

            setLocalFacility((prev) => ({
                ...prev,
                services: [...(prev?.services || []), data.service],
            }));

            resetNewServiceForm();
            setShowForm(false);
        } catch (e) {
            console.error(e);
            alert("Failed to add service.");
        }
    };

    if (!facility) return null;

    const hasWarning = !!facility?.warning;
    const warningTitle = facility?.warning?.title || "Warning";
    const warningNotes = facility?.warning?.notes || "";

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: hasWarning
                    ? {
                        border: 2,
                        borderColor: "warning.main",
                        boxShadow: (t) =>
                            `0 0 0 3px ${t.palette.warning.light}55`,
                    }
                    : undefined,
            }}
        >
            <DialogTitle
                sx={{
                    pb: 0,
                    ...(hasWarning
                        ? {
                            bgcolor: "warning.light",
                            borderBottom: (t) =>
                                `1px solid ${t.palette.warning.main}`,
                        }
                        : {}),
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {hasWarning && (
                                <WarningAmberIcon
                                    fontSize="small"
                                    color="warning"
                                />
                            )}
                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                                Facility Service Details
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            <strong>{facility.facility_name}</strong> (
                            {facility.state}) • Corp: {facility.corp_name} •
                            City: {facility.city} • Provider #:{" "}
                            {facility.source_id || "N/A"}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            startIcon={<AddIcon />}
                            variant={showForm ? "outlined" : "contained"}
                            onClick={handleToggleNewForm}
                        >
                            {showForm ? "Close Form" : "Add Service"}
                        </Button>
                        <Tooltip title="Close">
                            <IconButton onClick={handleDialogClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                {hasWarning && (
                    <Alert
                        severity="warning"
                        icon={<WarningAmberIcon />}
                        sx={{ mb: 2 }}
                    >
                        <strong>{warningTitle}:</strong> {warningNotes}
                    </Alert>
                )}

                {!!facility?.possible_corp_change && (
                    <Alert
                        severity="info"
                        sx={{ mb: 2 }}
                        icon={<CompareArrowsIcon />}
                    >
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Possible corp change
                            </Typography>

                            <Typography variant="body2">
                                <strong>Facility IDs:</strong>{" "}
                                {formatIdList(facility.possible_corp_change.matched_facility_unique_ids)}
                            </Typography>

                        </Stack>
                    </Alert>
                )}

                {!!facility?.deactivated_api && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        icon={<BlockIcon />}
                    >
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Deactivated API
                            </Typography>

                            <Typography variant="body2">
                                {buildDeactivatedApiBits(facility.deactivated_api).join(" • ") || "Deactivated API flag present"}
                            </Typography>
                        </Stack>
                    </Alert>
                )}



                {/* Add Service Form */}
                {showForm && (
                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardContent sx={{ pt: 2 }}>
                            <Grid
                                container
                                spacing={2}
                                alignItems="center"
                            >
                                {/* Service */}
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="medium"
                                        label="Service"
                                        value={formData.service_name || ""}
                                        onChange={(e) =>
                                            setField(
                                                "service_name",
                                                e.target.value
                                            )
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{
                                            displayEmpty: true,
                                            autoWidth: false,
                                            renderValue: (selected) =>
                                                selected
                                                    ? selected
                                                    : "Select service…",
                                            MenuProps: {
                                                PaperProps: {
                                                    style: { maxHeight: 320 },
                                                },
                                            },
                                        }}
                                        sx={{
                                            minWidth: {
                                                xs: 220,
                                                md: "unset",
                                            },
                                            "& .MuiSelect-select": {
                                                minHeight: 40,
                                            },
                                        }}
                                    >
                                        <MenuItem disabled value="">
                                            <em>Select service…</em>
                                        </MenuItem>
                                        {serviceOptions.map((opt) => {
                                            const statusOf = (s) =>
                                                String(
                                                    s.service_status || ""
                                                )
                                                    .trim()
                                                    .toLowerCase();
                                            const isBlocking = (s) =>
                                                statusOf(s) === "active" ||
                                                statusOf(s) ===
                                                "pending approval" ||
                                                statusOf(s) === "on hold" ||
                                                (!!s.start_date &&
                                                    !s.stop_date &&
                                                    !s.on_hold_date);

                                            const inUseBlocking = (
                                                localFacility?.services || []
                                            ).some(
                                                (s) =>
                                                    s.service_name === opt &&
                                                    isBlocking(s)
                                            );

                                            return (
                                                <MenuItem
                                                    key={opt}
                                                    value={opt}
                                                    disabled={inUseBlocking}
                                                >
                                                    {opt}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Grid>

                                {/* Start Date */}
                                <Grid item xs={12} md={2}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label="Start Date"
                                            value={formData.start_date}
                                            onChange={(v) =>
                                                setField("start_date", v)
                                            }
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: "medium",
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                {/* Rep */}
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        label="Rep Name"
                                        fullWidth
                                        size="medium"
                                        value={formData.rep_name}
                                        onChange={(e) =>
                                            setField("rep_name", e.target.value)
                                        }
                                    />
                                </Grid>

                                {/* Original Creator */}
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        label="Original Creator"
                                        fullWidth
                                        size="medium"
                                        value={formData.original_creator}
                                        onChange={(e) =>
                                            setField(
                                                "original_creator",
                                                e.target.value
                                            )
                                        }
                                    />
                                </Grid>

                                {/* Notes */}
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Notes"
                                        fullWidth
                                        size="medium"
                                        multiline
                                        minRows={1}
                                        value={formData.notes}
                                        onChange={(e) =>
                                            setField("notes", e.target.value)
                                        }
                                    />
                                </Grid>

                                {/* Actions */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1fr auto",
                                            width: "100%",
                                        }}
                                    >
                                        <span />
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={handleCancelNew}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={handleSubmitNew}
                                            >
                                                Submit
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Services List */}
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Services
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {[...groupedServices.entries()].map(
                    ([serviceName, instances]) => {
                        const latest = instances[0];
                        const latestNotes = (
                            latest?.notes?.note || []
                        )
                            .slice()
                            .sort(
                                (a, b) =>
                                    new Date(b.date) - new Date(a.date)
                            );
                        const latestNote = latestNotes[0];

                        return (
                            <Accordion
                                key={serviceName}
                                expanded={
                                    expandedService === serviceName
                                }
                                onChange={() =>
                                    setExpandedService((p) =>
                                        p === serviceName
                                            ? null
                                            : serviceName
                                    )
                                }
                                disableGutters
                                elevation={0}
                                sx={{
                                    mb: 1.5,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    "&:before": { display: "none" },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Stack
                                        sx={{ width: "100%" }}
                                        spacing={0.5}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            flexWrap="wrap"
                                            gap={1}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {serviceName}
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                flexWrap="wrap"
                                            >
                                                <Chip
                                                    size="small"
                                                    label={statusLabel(
                                                        latest.service_status
                                                    )}
                                                    color={statusColor(
                                                        latest.service_status
                                                    )}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={`Start ${prettyDate(
                                                        latest.start_date
                                                    )}${
                                                        latest.stop_date
                                                            ? ` • Stop ${prettyDate(
                                                                latest.stop_date
                                                            )}`
                                                            : ""
                                                    }${
                                                        latest.on_hold_date
                                                            ? ` • On Hold ${prettyDate(
                                                                latest.on_hold_date
                                                            )}`
                                                            : ""
                                                    }`}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    size="small"
                                                    icon={<HistoryIcon />}
                                                    variant="outlined"
                                                    label={`${instances.length} total`}
                                                />
                                            </Stack>
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <NotesIcon fontSize="small" />
                                            {latestNote
                                                ? `${latestNote.text} — ${
                                                    latestNote.creator ||
                                                    "Unknown"
                                                } • ${prettyDate(
                                                    latestNote.date
                                                )}`
                                                : "No notes"}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <List dense disablePadding>
                                        {instances.map((entry, i) => {
                                            const sortedNotes =
                                                entry.notes?.note
                                                    ? [
                                                        ...entry.notes
                                                            .note,
                                                    ].sort(
                                                        (a, b) =>
                                                            new Date(
                                                                b.date
                                                            ) -
                                                            new Date(
                                                                a.date
                                                            )
                                                    )
                                                    : [];

                                            return (
                                                <Box
                                                    key={
                                                        entry.id || i
                                                    }
                                                    sx={{
                                                        p: 1.5,
                                                        border: 1,
                                                        borderColor:
                                                            "divider",
                                                        borderRadius: 1,
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="start"
                                                        gap={2}
                                                        flexWrap="wrap"
                                                    >
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                <strong>
                                                                    Start:
                                                                </strong>{" "}
                                                                {dayjs(
                                                                    entry.start_date
                                                                ).format(
                                                                    "YYYY-MM-DD HH:mm"
                                                                )}
                                                                {entry.stop_date && (
                                                                    <>
                                                                        {
                                                                            "  •  "
                                                                        }
                                                                        <strong>
                                                                            Stop:
                                                                        </strong>{" "}
                                                                        {dayjs(
                                                                            entry.stop_date
                                                                        ).format(
                                                                            "YYYY-MM-DD HH:mm"
                                                                        )}
                                                                    </>
                                                                )}
                                                                {entry.on_hold_date && (
                                                                    <>
                                                                        {
                                                                            "  •  "
                                                                        }
                                                                        <strong>
                                                                            On
                                                                            Hold:
                                                                        </strong>{" "}
                                                                        {prettyDate(
                                                                            entry.on_hold_date
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                <strong>
                                                                    Rep:
                                                                </strong>{" "}
                                                                {entry.rep ||
                                                                    "—"}
                                                                {"  •  "}
                                                                <strong>
                                                                    Original
                                                                    Creator:
                                                                </strong>{" "}
                                                                {entry.original_creator ||
                                                                    "—"}
                                                            </Typography>
                                                        </Box>

                                                        {/* Always allow update so you can restart ended services */}
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={
                                                                <EditIcon />
                                                            }
                                                            onClick={() =>
                                                                setUpdateServiceForms(
                                                                    (
                                                                        prev
                                                                    ) => {
                                                                        const open =
                                                                            !!prev[
                                                                                entry
                                                                                    .id
                                                                                ];
                                                                        return {
                                                                            ...prev,
                                                                            [entry.id]:
                                                                                open
                                                                                    ? null
                                                                                    : {
                                                                                        dateMode:
                                                                                            "none", // 'none' | 'restart' | 'stop' | 'hold'
                                                                                        selected_date:
                                                                                            null,
                                                                                        creator:
                                                                                            "",
                                                                                        notes: "",
                                                                                    },
                                                                        };
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            {updateServiceForms[
                                                                entry.id
                                                                ]
                                                                ? "Cancel"
                                                                : "Update"}
                                                        </Button>
                                                    </Stack>

                                                    {sortedNotes.length >
                                                        0 && (
                                                            <Box mt={1}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    Notes
                                                                </Typography>
                                                                <List
                                                                    dense
                                                                    sx={{
                                                                        pt: 0,
                                                                    }}
                                                                >
                                                                    {sortedNotes.map(
                                                                        (
                                                                            note,
                                                                            idx
                                                                        ) => (
                                                                            <ListItem
                                                                                key={
                                                                                    idx
                                                                                }
                                                                                disableGutters
                                                                                sx={{
                                                                                    py: 0.5,
                                                                                }}
                                                                            >
                                                                                <ListItemText
                                                                                    primaryTypographyProps={{
                                                                                        variant:
                                                                                            "body2",
                                                                                    }}
                                                                                    secondaryTypographyProps={{
                                                                                        variant:
                                                                                            "caption",
                                                                                        color:
                                                                                            "text.secondary",
                                                                                    }}
                                                                                    primary={`${dayjs(
                                                                                        note.date
                                                                                    ).format(
                                                                                        "YYYY-MM-DD HH:mm"
                                                                                    )} — ${
                                                                                        note.creator ||
                                                                                        "Unknown"
                                                                                    }`}
                                                                                    secondary={
                                                                                        note.text
                                                                                    }
                                                                                />
                                                                            </ListItem>
                                                                        )
                                                                    )}
                                                                </List>
                                                            </Box>
                                                        )}

                                                    {updateServiceForms[
                                                        entry.id
                                                        ] && (
                                                        <Box mt={1.5}>
                                                            <Grid
                                                                container
                                                                spacing={1.5}
                                                                alignItems="center"
                                                            >
                                                                {/* Date Type select */}
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                    md={4}
                                                                >
                                                                    <TextField
                                                                        select
                                                                        size="small"
                                                                        fullWidth
                                                                        label="Date type"
                                                                        value={
                                                                            updateServiceForms[
                                                                                entry
                                                                                    .id
                                                                                ]
                                                                                .dateMode ||
                                                                            "none"
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) => {
                                                                            const mode =
                                                                                e
                                                                                    .target
                                                                                    .value; // 'none' | 'restart' | 'stop' | 'hold'
                                                                            setUpdateServiceForms(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [entry.id]:
                                                                                        {
                                                                                            ...prev[
                                                                                                entry
                                                                                                    .id
                                                                                                ],
                                                                                            dateMode:
                                                                                            mode,
                                                                                            selected_date:
                                                                                                null,
                                                                                        },
                                                                                })
                                                                            );
                                                                        }}
                                                                    >
                                                                        <MenuItem value="none">
                                                                            <em>
                                                                                None
                                                                            </em>
                                                                        </MenuItem>
                                                                        <MenuItem value="restart">
                                                                            Restart
                                                                            Service
                                                                        </MenuItem>
                                                                        <MenuItem value="stop">
                                                                            End
                                                                            Date
                                                                        </MenuItem>
                                                                        <MenuItem value="hold">
                                                                            On
                                                                            Hold
                                                                            Date
                                                                        </MenuItem>
                                                                    </TextField>
                                                                </Grid>

                                                                {/* Single date picker */}
                                                                {updateServiceForms[
                                                                        entry.id
                                                                        ].dateMode !==
                                                                    "none" && (
                                                                        <Grid
                                                                            item
                                                                            xs={12}
                                                                            sm={6}
                                                                            md={4}
                                                                        >
                                                                            <LocalizationProvider
                                                                                dateAdapter={
                                                                                    AdapterDayjs
                                                                                }
                                                                            >
                                                                                <DatePicker
                                                                                    label={
                                                                                        updateServiceForms[
                                                                                            entry
                                                                                                .id
                                                                                            ]
                                                                                            .dateMode ===
                                                                                        "restart"
                                                                                            ? "New Start Date"
                                                                                            : updateServiceForms[
                                                                                                entry
                                                                                                    .id
                                                                                                ]
                                                                                                .dateMode ===
                                                                                            "stop"
                                                                                                ? "End Date"
                                                                                                : "On Hold Date"
                                                                                    }
                                                                                    value={
                                                                                        updateServiceForms[
                                                                                            entry
                                                                                                .id
                                                                                            ]
                                                                                            .selected_date
                                                                                    }
                                                                                    onChange={(
                                                                                        v
                                                                                    ) =>
                                                                                        handleUpdateServiceChange(
                                                                                            entry.id,
                                                                                            "selected_date",
                                                                                            v
                                                                                        )
                                                                                    }
                                                                                    slotProps={{
                                                                                        textField:
                                                                                            {
                                                                                                fullWidth: true,
                                                                                                size: "small",
                                                                                            },
                                                                                    }}
                                                                                />
                                                                            </LocalizationProvider>
                                                                        </Grid>
                                                                    )}

                                                                {/* Creator */}
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                    md={4}
                                                                >
                                                                    <TextField
                                                                        label="Creator"
                                                                        size="small"
                                                                        fullWidth
                                                                        value={
                                                                            updateServiceForms[
                                                                                entry
                                                                                    .id
                                                                                ]
                                                                                .creator
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleUpdateServiceChange(
                                                                                entry.id,
                                                                                "creator",
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>

                                                                {/* Notes */}
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                >
                                                                    <TextField
                                                                        label="Notes"
                                                                        required
                                                                        multiline
                                                                        minRows={
                                                                            3
                                                                        }
                                                                        fullWidth
                                                                        value={
                                                                            updateServiceForms[
                                                                                entry
                                                                                    .id
                                                                                ]
                                                                                .notes
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleUpdateServiceChange(
                                                                                entry.id,
                                                                                "notes",
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>

                                                                {/* Submit */}
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        justifyContent="flex-end"
                                                                        spacing={1}
                                                                    >
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleUpdateServiceSubmit(
                                                                                    entry.id
                                                                                )
                                                                            }
                                                                        >
                                                                            Submit
                                                                        </Button>
                                                                    </Stack>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        );
                    }
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleDialogClose}
                    variant="outlined"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
